/**
 * @file Fits text within an element by adjusting its font size
 */

interface FitOptions {
  minSize: number
  maxSize: number
  multiLine: boolean
  letterSpacing?: number
  maxLines?: number
  lineHeight?: number
}

const defaultOptions: FitOptions = {
  minSize: 16,
  maxSize: 512,
  multiLine: true,
}

function calculateFontSize(element: HTMLElement, currentFontSize: number, options: FitOptions): number {
  const currentWidth = element.scrollWidth

  if (!Number.isFinite(currentFontSize) || currentFontSize <= 0) return options.minSize
  if (currentWidth <= 0) return options.minSize

  let availableWidth = (element.parentNode as HTMLElement)?.clientWidth ?? 0

  if (options.letterSpacing != null) {
    // Subtract the letter spacing from the available width
    if (element.textContent?.length && element.textContent.length > 1) {
      const totalLetterSpacingWidth = (element.textContent.length - 1) * options.letterSpacing
      availableWidth = availableWidth - totalLetterSpacingWidth
    }
  }

  if (availableWidth <= 0) return options.minSize

  // If the element is wider than the available width, we need to fit it within the available width
  const ratio = availableWidth / currentWidth
  const nextSize = ratio * currentFontSize
  if (!Number.isFinite(nextSize)) return options.minSize

  return Math.min(Math.max(options.minSize, nextSize), options.maxSize)
}

function applyStyles(element: HTMLElement, fontSize: number, settings: FitOptions): void {
  element.style.wordBreak = 'normal'
  element.style.overflowWrap = 'anywhere'
  element.style.whiteSpace = settings.multiLine && fontSize === settings.minSize ? 'normal' : 'nowrap'
  element.style.display = 'inline-block'
  element.style.fontSize = `${fontSize}px`
}

/**
 * Fits text within an element by adjusting its font size
 * @param element The element to fit text within
 * @param options Configuration options
 * @returns A cleanup function to reset the element's styles
 */
function fitText(element: HTMLElement, options: Partial<FitOptions> = {}): () => void {
  const settings = { ...defaultOptions, ...options }
  const style = element.style as unknown as Record<string, string>

  // Store original styles for cleanup
  const originalStyle = {
    whiteSpace: element.style.whiteSpace,
    display: element.style.display,
    fontSize: element.style.fontSize,
    wordBreak: element.style.wordBreak,
    overflowWrap: element.style.overflowWrap,
    webkitBoxOrient: style.webkitBoxOrient,
    webkitLineClamp: style.webkitLineClamp,
    maxHeight: element.style.maxHeight,
  }

  // Multi-line fitting with maxLines constraint
  if (settings.maxLines != null) {
    const getLineHeight = (fallbackFontSize: number) => {
      const computedLineHeight = parseFloat(window.getComputedStyle(element).lineHeight)
      if (Number.isFinite(computedLineHeight) && computedLineHeight > 0) return computedLineHeight
      if (settings.lineHeight != null && settings.lineHeight > 0) return settings.lineHeight
      return fallbackFontSize
    }

    // Set up for wrapping and line-clamp
    element.style.display = '-webkit-box'
    style.webkitBoxOrient = 'vertical'
    style.webkitLineClamp = settings.maxLines.toString()
    element.style.wordBreak = 'normal'
    element.style.overflowWrap = 'anywhere'
    element.style.whiteSpace = 'normal'

    // Binary search for optimal font size
    let minSize = settings.minSize
    let maxSize = settings.maxSize
    let bestSize = minSize

    for (let i = 0; i < 15 && minSize <= maxSize; i++) {
      const testSize = Math.floor((minSize + maxSize) / 2)
      element.style.fontSize = `${testSize}px`
      void element.offsetHeight // Force reflow

      const maxHeight = getLineHeight(testSize) * settings.maxLines
      element.style.maxHeight = `${maxHeight}px`

      if (element.scrollHeight <= maxHeight) {
        bestSize = testSize
        minSize = testSize + 1
      } else {
        maxSize = testSize - 1
      }
    }

    element.style.fontSize = `${bestSize}px`
    void element.offsetHeight // Force reflow
    element.style.maxHeight = `${getLineHeight(bestSize) * settings.maxLines}px`
  } else {
    // Single-line fitting (original behavior)
    applyStyles(element, settings.maxSize, settings)
    const computedSize = parseFloat(window.getComputedStyle(element).fontSize)
    const newFontSize = calculateFontSize(element, computedSize, settings)
    applyStyles(element, newFontSize, settings)
  }

  // Return cleanup function
  return () => {
    const cleanupStyle = element.style as unknown as Record<string, string>
    element.style.whiteSpace = originalStyle.whiteSpace
    element.style.display = originalStyle.display
    element.style.fontSize = originalStyle.fontSize
    element.style.wordBreak = originalStyle.wordBreak
    element.style.overflowWrap = originalStyle.overflowWrap
    element.style.maxHeight = originalStyle.maxHeight
    if (originalStyle.webkitBoxOrient !== undefined) {
      cleanupStyle.webkitBoxOrient = originalStyle.webkitBoxOrient
    } else {
      delete cleanupStyle.webkitBoxOrient
    }
    if (originalStyle.webkitLineClamp !== undefined) {
      cleanupStyle.webkitLineClamp = originalStyle.webkitLineClamp
    } else {
      delete cleanupStyle.webkitLineClamp
    }
  }
}

export type { FitOptions }
export { fitText }
